import {
  Badge,
  Button,
  type ComboBoxOption,
  FormComboBox,
  RadioCard,
  RequiredFieldsNote,
  Slider,
  Text,
} from '@civilgrid/ui/components';
import { isBillingTierEnabled } from '@civilgrid/utils/src/common/featureAccess';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { buffer } from '@turf/turf';
import type { MultiLineString } from 'geojson';
import { map } from 'lodash';
import type { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncRetry } from 'react-use';
import type { AsyncState } from 'react-use/lib/useAsyncFn';
import { useSnapshot } from 'valtio';
import { newProject } from '~/api/projects2';
import authorizations, { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import type { GetCostOutput } from '~/common/billingUtils';
import { createCircleFromPoint } from '~/common/geoUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { getProjectCenter, getRequiredFieldsForBillingTier } from '~/common/projectUtils';
import { ORG_SWITCHER_PROJECTS_KEY } from '~/common/queryKeys';
import { type CircleObject, type LineObject, type MapObject, type Project, ProjectGeometryType } from '~/common/types';
import InvalidGeometryBadge from '~/components2/Badge/InvalidGeometryBadge';
import ControlledDatePicker from '~/components2/forms/controlled/ControlledDatePicker';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import Icon from '~/components2/Icon';
import { Notify } from '~/components2/Notify';
import { useValidateGeometry } from '~/hooks/useValidateGeometry';
import { homeMapStore as store } from '~/pages/homeViewV2/store';
import { projectTypeOptions } from '~/pages/projectDetails/details/common';
import Section from '~/pages/projectDetails/details/Section';
import rootStore from '~/store';
import { CompareProjectTypesDialog, CostBreakdownDialog } from '../ProjectTypeInfoDialogs';
import ProjectTypePill from '../ProjectTypePill';
import { getStreetAndIntersection } from '../TopNav/UniversalSearch/searchUtils';
import { exitProjectCreationMode } from '.';
import { PROGRAM_MANAGER_FLAG, PROJECT_AREA_LIMIT, placeholderAndTooltips } from './common';

interface NewProjectForm {
  scope?: string;
  name: string;
  type?: string;
  detail?: string;
  projectStatus?: string;
  projectPhase?: string;
  projectManagerId?: string;
  programManagerId?: string;
  subProgramManagerId?: string;
  startDate?: DateTime;
  endDate?: DateTime;
  constructionStartDate?: DateTime;
  constructionEndDate?: DateTime;
  budget?: number;
  billingCode: string;
  billingCode2: string;
  client: string;
  address2: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    crossStreets: string;
  };
  projectVicinity: string;
  billingTier: Project['billingTier'];
  peName: string;
  peLicenseNumber: string;
  peLicenseState: string;
}

interface Props {
  lengthAndArea: {
    length: { value: number; unit: string };
    area: { value: number; unit: string };
  };
  geomAsGeoJSON: GeoJSON.MultiLineString | GeoJSON.MultiPolygon;
  projectCost: AsyncState<GetCostOutput>;
}

export default function NewProjectDetails({ geomAsGeoJSON, lengthAndArea, projectCost }: Props) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganization, me } = useSnapshot(rootStore) as typeof rootStore;
  // Hide billing tiers the org has disabled (enabledBillingTiers override).
  const tierSubscription = currentOrganization?.subscription;
  const tierDraftEnabled = isBillingTierEnabled('draft', tierSubscription);
  const tierProposalEnabled = isBillingTierEnabled('proposal', tierSubscription);
  const tierProjectEnabled = isBillingTierEnabled('project', tierSubscription);
  const tierProjectPlusEnabled = isBillingTierEnabled('projectPlus', tierSubscription);
  const tierColsClass = (
    { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' } as Record<number, string>
  )[[tierDraftEnabled, tierProposalEnabled, tierProjectEnabled, tierProjectPlusEnabled].filter(Boolean).length];
  const canCreateProject = authorizations.can(Action.CreateProject);
  const { geometryType, objectsAsArray, isGeometryFromKMZ, kmzFileName, geometryAsMultiPolygon } = useSnapshot(
    store,
  ) as typeof store;

  const { handleSubmit, control, setValue, trigger, watch, formState } = useForm<NewProjectForm>({
    defaultValues: {
      billingTier: 'draft',
      address2: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        crossStreets: '',
      },
    },
  });
  // read isValid unconditionally every render: formState is a Proxy that only subscribes to
  // properties actually read, and short-circuiting in the save button's disabled expression
  // would otherwise skip the read and leave isValid permanently stale
  const { isValid } = formState;
  const programManagerEnabled = useFeatureFlagEnabled(PROGRAM_MANAGER_FLAG);
  const projectManagerId = watch('projectManagerId');
  // The id the auto-assign effect last wrote, and whether the creator has since moved off it.
  const autoAssignedProjectManagerIdRef = useRef<string | null>(null);
  const creatorChangedProjectManagerRef = useRef(false);

  const concStart = watch('constructionStartDate');
  const concEnd = watch('constructionEndDate');
  const startDate = watch('startDate');

  // If the org has disabled the default/selected billing tier, fall back to the first enabled tier
  // so creation never starts on an unavailable tier.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to subscription changes
  useEffect(() => {
    if (!isBillingTierEnabled(watch('billingTier'), tierSubscription)) {
      const firstEnabled = (['draft', 'proposal', 'project', 'projectPlus'] as const).find((t) =>
        isBillingTierEnabled(t, tierSubscription),
      );
      if (firstEnabled) {
        setValue('billingTier', firstEnabled);
      }
    }
  }, [tierSubscription]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    trigger('constructionEndDate');
  }, [concStart, concEnd]);

  const validationResult = useValidateGeometry({
    geometry: geometryAsMultiPolygon,
  });

  const orgMemberOptions = useMemo<ComboBoxOption[]>(() => {
    if (!currentOrganization) return [];
    return currentOrganization.members
      .filter((m) => m.status === 'active')
      .map((m) => ({ value: m.user.id, label: m.user.name, description: m.user.email }));
  }, [currentOrganization]);

  // auto assign project manager to the current user if they are an active org member
  useEffect(() => {
    const { members } = currentOrganization ?? {};

    const isActiveMember = members?.some((m) => m.user.id === me?.id && m.status === 'active');

    // if the current user is an active org member -> auto assign them as PM
    if (isActiveMember) {
      autoAssignedProjectManagerIdRef.current = me?.id ?? null;
      setValue('projectManagerId', me?.id, { shouldValidate: true, shouldDirty: true });
    }
  }, [me?.id, setValue, currentOrganization]);

  // Autopopulate project name from the uploaded KMZ filename
  useEffect(() => {
    if (isGeometryFromKMZ && kmzFileName && !watch('name')) {
      setValue('name', kmzFileName, { shouldDirty: true, shouldValidate: true });
    }
  }, [isGeometryFromKMZ, kmzFileName, setValue, watch]);

  // Sticky: once the selection leaves the auto-assigned default it stays "creator picked", so
  // choosing yourself again after picking someone else still reads as a deliberate choice.
  useEffect(() => {
    // Skip the empty field: on the commit that auto-assigns, the ref is already set but the watched
    // value has not caught up yet, which would otherwise read as a divergence.
    if (!projectManagerId) return;

    if (projectManagerId !== autoAssignedProjectManagerIdRef.current) {
      creatorChangedProjectManagerRef.current = true;
    }
  }, [projectManagerId]);

  useAsyncRetry(async () => {
    const c = getProjectCenter(geomAsGeoJSON);

    try {
      const geoResponse = await getStreetAndIntersection(c[0], c[1]);

      setValue('address2.street', geoResponse.street);
      setValue('address2.city', geoResponse.city);
      setValue('address2.state', geoResponse.state);
      setValue('address2.zipCode', geoResponse.zip);
      setValue('address2.crossStreets', geoResponse.intersection);
    } catch (e) {
      console.error('reverseGeocode failed', e, c);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const saveButtonLabel = useMemo(() => {
    if (watch('billingTier') === 'draft') {
      return 'Save as Draft';
    } else if (watch('billingTier') === 'proposal') {
      return 'Create Project';
    } else {
      return 'Create Project';
    }
  }, [watch('billingTier')]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const shouldDisableSave = useMemo(() => {
    if (projectCost.loading) {
      return true;
    }
    if (objectsAsArray.length === 0) {
      return true;
    }
    return lengthAndArea.area.value > PROJECT_AREA_LIMIT;
  }, [geometryType, lengthAndArea, projectCost]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const dimsEl = useMemo(() => {
    const out = [];
    if (objectsAsArray.length > 1) {
      // no need to display 'cumulative..'
      out.push(
        <React.Fragment key="dims">
          {geometryType === ProjectGeometryType.Linear && (
            <div className="flex justify-between font-noto">
              <Text className="text-sm">Cumulative length</Text>
              <Text className="text-sm font-bold">
                {lengthAndArea.length.value.toLocaleString()} {lengthAndArea.length.unit}
              </Text>
            </div>
          )}
          <div className="flex justify-between font-noto">
            <Text className="text-sm">Cumulative area</Text>
            <Text className="text-sm font-bold">
              {lengthAndArea.area.value.toLocaleString()} {lengthAndArea.area.unit}
            </Text>
          </div>
        </React.Fragment>,
      );
    }

    if (geometryType === ProjectGeometryType.Linear) {
      out.push(
        <Slider
          className="font-noto"
          label="Linear buffer"
          tooltipText="The buffer width is added to each side of the line."
          min={10}
          max={150}
          step={1}
          value={(objectsAsArray[0] as LineObject)!.properties.buffer || store.lineBuffer}
          formatValue={(value) => `${value} ft`}
          onValueChange={(value) => {
            // update buffer for existing objects
            store.objectsAsArray.forEach((o: MapObject) => {
              (o as LineObject).properties.buffer = value;
            });
            store.shouldRedraw = true;

            // set buffer for objects that will be created
            store.updateLineBuffer(value);
          }}
          data-testid="new-project-linear-buffer-slider"
        />,
      );
    }

    if (geometryType === ProjectGeometryType.Circle) {
      out.push(<RadiusSlider />);
    }

    if (out.length > 0) {
      return <div className="flex flex-col space-y-6 border p-4 font-noto text-base">{out}</div>;
    } else {
      return null;
    }
  }, [objectsAsArray, geometryType]);

  const requiredFieldsList = getRequiredFieldsForBillingTier(watch('billingTier'));

  const onSubmit: SubmitHandler<NewProjectForm> = async (data) => {
    try {
      setIsProcessing(true);

      let projectData: any = {
        ...data,
        geometryType,
        costInfo: projectCost.value?.[data.billingTier],
      };

      if (geometryType === ProjectGeometryType.Linear) {
        const multiLine = geomAsGeoJSON as MultiLineString;
        const bufferOffset = (objectsAsArray![0] as LineObject).properties.buffer || 40;
        let bufferedPolygon = buffer(multiLine, bufferOffset, {
          units: 'feet',
        })?.geometry;
        // all polygons need to be MultiPolygon
        if (bufferedPolygon!.type === 'Polygon') {
          bufferedPolygon = {
            type: 'MultiPolygon',
            coordinates: [bufferedPolygon!.coordinates],
          };
        }

        projectData = {
          ...projectData,
          geometry: bufferedPolygon,
          linear: {
            geometry: geomAsGeoJSON,
            buffer: {
              offset: bufferOffset,
              units: 'feet',
            },
          },
        };
      } else if (geometryType === ProjectGeometryType.Circle) {
        const circleMultiPolygon: GeoJSON.MultiPolygon = {
          type: 'MultiPolygon',
          coordinates: map(
            objectsAsArray as CircleObject[],
            (o) =>
              createCircleFromPoint({
                center: o.geometry,
                radius: o.properties.radius,
              }).coordinates,
          ),
        };

        const circleMultiPoint: GeoJSON.MultiPoint = {
          type: 'MultiPoint',
          coordinates: map(objectsAsArray, (o) => (o as CircleObject).geometry.coordinates),
        };

        const radiusInFeet = (objectsAsArray[0] as CircleObject).properties.radius;
        const circleData: Project['circle'] = {
          geometry: circleMultiPoint,
          radius: { units: 'feet', value: radiusInFeet },
        };

        projectData = {
          ...projectData,
          geometry: circleMultiPolygon,
          circle: { ...circleData },
        };
      } else {
        projectData = {
          ...projectData,
          geometry: geomAsGeoJSON,
        };
      }

      const createdProject = await newProject(projectData, rootStore.currentOrganizationId);
      // clear cache for global projects list
      queryClient.invalidateQueries({ queryKey: ORG_SWITCHER_PROJECTS_KEY });
      // navigate to project detail page
      navigate({ to: '/projects/$projectId', params: { projectId: createdProject.id } });
      exitProjectCreationMode();

      capturePosthogEvent(POSTHOG_EVENTS.newProjectCreated, {
        project_id: createdProject.id,
        project_name: createdProject.name,
        project_type: data.billingTier,
        geometry_type: geometryType ?? '',
        project_cost: projectCost.value?.[data.billingTier].totalCost ?? 0,
        area: lengthAndArea.area.value,
        length: lengthAndArea.length.value,
        from_kmz: isGeometryFromKMZ,
        project_manager_assigned: Boolean(data.projectManagerId),
        // False when the creator overrode the auto-assigned default before submitting.
        project_manager_is_creator: Boolean(me?.id) && data.projectManagerId === me?.id,
        // The pair above cannot separate an auto-assignment from a deliberate self-pick; this can.
        // Null rather than a source when the project was created with no project manager at all.
        project_manager_source: data.projectManagerId
          ? creatorChangedProjectManagerRef.current
            ? 'creator_selected'
            : 'auto_assigned'
          : null,
      });
    } catch (e) {
      console.error('Error creating project: ', e);
      setIsError(true);
      Notify.error({
        message: 'An error occurred while creating the project. Please contact support.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {dimsEl}
      <div className="mb-40 w-full">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {requiredFieldsList.length > 0 && (
            <RequiredFieldsNote className="mb-4" data-testid="new-project-required-fields-note" />
          )}
          <Section
            title={
              <div className="flex items-center justify-between font-noto">
                <Text asChild className="text-base font-semibold">
                  <h3>Select project type</h3>
                </Text>
                <div className="flex flex-col items-end">
                  <CompareProjectTypesDialog projectCost={projectCost} testIdPrefix="new-project" className="w-3/4" />
                  <CostBreakdownDialog projectCost={projectCost} testIdPrefix="new-project" className="w-[480px]" />
                </div>
              </div>
            }
          >
            <RadioCard
              aria-label="Select project type"
              aria-busy={projectCost.loading ? true : undefined}
              className={tierColsClass}
              value={watch('billingTier')}
              onValueChange={(v) => setValue('billingTier', v, { shouldDirty: true })}
            >
              {tierDraftEnabled && (
                <ProjectTypePill
                  billingTier="draft"
                  label="Draft"
                  cost={projectCost}
                  zeroCostLabel="Free"
                  data-testid="new-project-billing-tier-draft"
                />
              )}
              {tierProposalEnabled && (
                <Can
                  action={Action.CreateProject}
                  message={
                    <div className="font-noto text-base">
                      <span className="block">Your current role is not authorized to create a project.</span>
                      <br />
                      <span className="block">Please contact your account admin.</span>
                      <div className="mt-4 flex items-center space-x-2 text-sm">
                        Click the
                        <span className="inline-block px-1">
                          <Icon icon="help2" />
                        </span>{' '}
                        icon in the top right to contact support.
                      </div>
                    </div>
                  }
                >
                  <ProjectTypePill
                    billingTier="proposal"
                    label="Project Lite"
                    cost={projectCost}
                    disabled={!canCreateProject}
                    data-testid="new-project-billing-tier-proposal"
                  />
                </Can>
              )}
              {tierProjectEnabled && (
                <Can
                  action={Action.CreateProject}
                  message={
                    <div className="font-noto text-base">
                      <span className="block">Your current role is not authorized to create a project.</span>
                      <br />
                      <span className="block">Please contact your account admin.</span>
                      <div className="mt-4 flex items-center space-x-2 text-sm">
                        If you need assistance or want to take a product tour, click the
                        <span className="inline-block px-1">
                          <Icon icon="help2" />
                        </span>
                        icon in the top right corner.
                      </div>
                    </div>
                  }
                >
                  <ProjectTypePill
                    billingTier="project"
                    label="Project"
                    cost={projectCost}
                    disabled={!canCreateProject}
                    data-testid="new-project-billing-tier-project"
                  />
                </Can>
              )}
              {tierProjectPlusEnabled && (
                <Can
                  action={Action.CreateProject}
                  message={
                    <div className="font-noto text-base">
                      <span className="block">Your current role is not authorized to create a project.</span>
                      <br />
                      <span className="block">Please contact your account admin.</span>
                      <div className="mt-4 flex items-center space-x-2 text-sm">
                        If you need assistance or want to take a product tour, click the
                        <span className="inline-block px-1">
                          <Icon icon="help2" />
                        </span>
                        icon in the top right corner.
                      </div>
                    </div>
                  }
                >
                  <ProjectTypePill
                    billingTier="projectPlus"
                    label="Project Plus"
                    cost={projectCost}
                    disabled={!canCreateProject}
                    data-testid="new-project-billing-tier-project-plus"
                  />
                </Can>
              )}
            </RadioCard>
          </Section>

          <div className="flex justify-start my-2">
            <InvalidGeometryBadge query={validationResult} />
          </div>

          <div className="space-y-6">
            <Section headingLevel={3} title="Project Overview">
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <div className="col-span-2">
                  <ControlledInput
                    label="Project Name"
                    control={control}
                    name="name"
                    isRequired={requiredFieldsList.includes('name')}
                    tooltipText={placeholderAndTooltips.projectNameTooltip}
                    data-testid="new-project-name"
                  />
                </div>
                <ControlledInput
                  label="Billing Code 1"
                  control={control}
                  name="billingCode"
                  isRequired={requiredFieldsList.includes('billingCode')}
                  tooltipText={placeholderAndTooltips.billingCodeTooltip}
                  placeholder={placeholderAndTooltips.billingCodePlaceholder}
                  data-testid="new-project-billing-code-1"
                />
                <ControlledInput
                  label="Billing Code 2"
                  control={control}
                  name="billingCode2"
                  isRequired={requiredFieldsList.includes('billingCode2')}
                  tooltipText={placeholderAndTooltips.billingCodeTooltip}
                  placeholder={placeholderAndTooltips.billingCodePlaceholder}
                  data-testid="new-project-billing-code-2"
                />
              </div>
            </Section>

            <Section headingLevel={3} title="Project Team">
              <div className="flex flex-col gap-2">
                <FormComboBox
                  control={control}
                  name="projectManagerId"
                  label="Project Manager"
                  options={orgMemberOptions}
                  isRequired={requiredFieldsList.includes('projectManagerId')}
                  tooltipText={placeholderAndTooltips.pmTooltip}
                  data-testid="new-project-project-manager"
                />
                {programManagerEnabled && (
                  <>
                    <FormComboBox
                      control={control}
                      name="programManagerId"
                      label="Program Manager"
                      options={orgMemberOptions}
                      isClearable
                      tooltipText={placeholderAndTooltips.programManagerTooltip}
                      data-testid="new-project-program-manager"
                    />
                    <FormComboBox
                      control={control}
                      name="subProgramManagerId"
                      label="Sub-Program Manager"
                      options={orgMemberOptions}
                      isClearable
                      tooltipText={placeholderAndTooltips.subProgramManagerTooltip}
                      data-testid="new-project-sub-program-manager"
                    />
                  </>
                )}
              </div>
            </Section>

            <Section
              headingLevel={3}
              tooltip={placeholderAndTooltips.projectNarrativeTooltip}
              title="Project Narrative"
            >
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <ControlledInput
                  control={control}
                  name="client"
                  label="Client"
                  placeholder="City of ABC"
                  data-testid="new-project-client"
                />
                <FormComboBox
                  control={control}
                  name="type"
                  label="Project Category"
                  options={projectTypeOptions}
                  data-testid="new-project-category"
                />

                <div className="col-span-2">
                  <ControlledTextArea
                    control={control}
                    label="Scope"
                    name="scope"
                    isRequired={requiredFieldsList.includes('scope')}
                    tooltipText={placeholderAndTooltips.scopeTooltip}
                    placeholder={placeholderAndTooltips.scopePlaceholder}
                    data-testid="new-project-scope"
                  />
                </div>
              </div>
            </Section>

            <Section headingLevel={3} title="Project Timelines">
              <div className="grid grid-cols-2 gap-2 gap-x-4">
                <ControlledDatePicker
                  control={control}
                  name="startDate"
                  label="Start Date"
                  data-testid="new-project-start-date"
                />
                <ControlledDatePicker
                  control={control}
                  name="endDate"
                  label="End Date"
                  minDate={watch('startDate')}
                  data-testid="new-project-end-date"
                />

                <ControlledDatePicker
                  control={control}
                  name="constructionStartDate"
                  label="Construction Start Date"
                  minDate={startDate}
                  data-testid="new-project-construction-start-date"
                />
                <ControlledDatePicker
                  control={control}
                  name="constructionEndDate"
                  label="Construction End Date"
                  minDate={concStart}
                  data-testid="new-project-construction-end-date"
                  validate={(value, formValues) => {
                    const start = formValues.constructionStartDate;
                    if (value && start && value < start) {
                      return 'Construction end date must be after construction start date.';
                    } else {
                      return true;
                    }
                  }}
                />
              </div>
            </Section>
          </div>

          {lengthAndArea.area.value > PROJECT_AREA_LIMIT && (
            <Badge color="red" className="my-2 px-4 py-2 text-sm">
              Maximum project size is {PROJECT_AREA_LIMIT.toLocaleString()} acres.
            </Badge>
          )}
          <div className="mt-10 flex items-center justify-end space-x-2">
            <Button
              variant="secondary"
              onClick={exitProjectCreationMode}
              // don't allow cancellation if the request is in progress, wait until resolved
              disabled={isProcessing}
              data-testid="new-project-discard"
            >
              Discard
            </Button>
            <Button
              type="submit"
              loading={isProcessing}
              disabled={shouldDisableSave || !isValid || isProcessing || isError || !validationResult.data?.isValid}
              tooltip={!validationResult.data?.isValid ? 'Invalid project geometry' : undefined}
              data-testid="new-project-save"
            >
              {saveButtonLabel}
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}

function RadiusSlider() {
  const { objectsAsArray, geometryType } = useSnapshot(store);
  if (!objectsAsArray.length) return null;
  if (geometryType !== ProjectGeometryType.Circle) return null;

  const circle = (objectsAsArray[0] as CircleObject)!;

  const r = circle.properties.radius / 5_280;
  const value = Number(r.toFixed(2));

  return (
    <Slider
      className="font-noto"
      label="Radius"
      min={0.1}
      max={1}
      step={0.05}
      value={value}
      formatValue={(value) => `${value} mi`}
      onValueChange={(value) => {
        (store.objectsAsArray as CircleObject[]).forEach((o) => {
          o.properties.radius = value * 5_280;
        });

        store.shouldRedraw = true;
      }}
      data-testid="new-project-radius-slider"
    />
  );
}
